import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Typography, IconButton, Tooltip, Box } from '@mui/material'
import { useAuth } from '@praxis/component-auth'
import ImageAnnotations, { ANNOTATION_MODES } from './ImageAnnotations'
import {
  RectangleOutlined,
  Edit,
  Highlight,
  CircleOutlined,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  OpenWith,
} from '@mui/icons-material'
import EggOutlinedIcon from '@mui/icons-material/EggOutlined'

const MODE_ICONS = {
  MOVE: OpenWith,
  RECTANGLE: RectangleOutlined,
  OVAL: EggOutlinedIcon,
  DRAWING: Edit,
  HIGHLIGHTER: Highlight,
  POINT: CircleOutlined,
}

const MODE_TOOLTIPS = {
  MOVE: 'Move & Resize',
  RECTANGLE: 'Rectangle',
  OVAL: 'Oval',
  DRAWING: 'Drawing',
  HIGHLIGHTER: 'Highlighter',
  POINT: 'Point',
}

const ACTIVE_TOOL_COLOR = '#6B7280'
const INACTIVE_TOOL_COLOR = '#4B5563'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4
const ZOOM_STEP = 0.2
const CANVAS_PADDING = 24
const DISPLAY_SCALE_FACTOR = 0.8
const CONTROLLER_BUTTON_SIZE = 40

const clampZoom = (value = 1) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value) || 1))

const getTouchDistance = (touchA, touchB) => {
  const dx = (touchA?.clientX || 0) - (touchB?.clientX || 0)
  const dy = (touchA?.clientY || 0) - (touchB?.clientY || 0)
  return Math.sqrt(dx * dx + dy * dy)
}

const getTouchMidpoint = (touchA, touchB) => ({
  x: ((touchA?.clientX || 0) + (touchB?.clientX || 0)) / 2,
  y: ((touchA?.clientY || 0) + (touchB?.clientY || 0)) / 2,
})

export default function ImageCard(props) {
  const {
    title = '',
    imageSrc = '',
    zoomSrc = '',
    // userDisplayName = '',
    enableAnnotations = false,
    annotations,
    onAnnotationsChange,
    annotationViewportHeight = '100%',
    annotationViewportStyle = {},
  } = props
  const auth = useAuth()
  const { session: { userInfo = {} } = {} } = auth
  const viewportRef = useRef(null)
  const containerRef = useRef(null)
  const panStateRef = useRef(null)
  const touchStateRef = useRef(null)
  const [localAnnotations, setLocalAnnotations] = useState([])
  const [mode, setMode] = useState('RECTANGLE')
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isPanning, setIsPanning] = useState(false)
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 })
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const useExternalAnnotations = typeof onAnnotationsChange === 'function'
  const resolvedAnnotations = useExternalAnnotations
    ? annotations || []
    : localAnnotations
  const handleAnnotationsChange = useExternalAnnotations
    ? onAnnotationsChange
    : setLocalAnnotations
  const handleZoom = () => {
    window.open(zoomSrc, '_blank')
  }

  const isAnnotationPanMode = enableAnnotations && mode === 'MOVE'

  const fittedScale = (() => {
    const availableWidth = Math.max(
      1,
      (viewportSize.width || 0) - CANVAS_PADDING * 2
    )
    const availableHeight = Math.max(
      1,
      (viewportSize.height || 0) - CANVAS_PADDING * 2
    )
    const { width: naturalWidth = 0, height: naturalHeight = 0 } = naturalSize
    if (!naturalWidth || !naturalHeight) return 1
    return Math.min(
      availableWidth / naturalWidth,
      availableHeight / naturalHeight,
      1
    )
  })()

  const baseWidth = Math.max(
    1,
    Math.round((naturalSize.width || 1) * fittedScale)
  )
  const baseHeight = Math.max(
    1,
    Math.round((naturalSize.height || 1) * fittedScale)
  )
  const displayWidth = Math.max(
    1,
    Math.round(baseWidth * zoomLevel * DISPLAY_SCALE_FACTOR)
  )
  const displayHeight = Math.max(
    1,
    Math.round(baseHeight * zoomLevel * DISPLAY_SCALE_FACTOR)
  )

  useEffect(() => {
    const updateSize = () => {
      const viewportNode = viewportRef.current
      if (!viewportNode) return
      setViewportSize({
        width: viewportNode.clientWidth,
        height: viewportNode.clientHeight,
      })
    }

    // Initial size update
    updateSize()

    // Update on window resize
    window.addEventListener('resize', updateSize)
    return () => {
      window.removeEventListener('resize', updateSize)
    }
  }, [])

  useEffect(() => {
    touchStateRef.current = null
    panStateRef.current = null
    setIsPanning(false)
    setZoomLevel(1)

    const viewportNode = viewportRef.current
    if (!viewportNode) return
    viewportNode.scrollLeft = 0
    viewportNode.scrollTop = 0
  }, [imageSrc])

  const updateZoom = useCallback(
    (nextZoomLevel, clientX, clientY) => {
      const viewportNode = viewportRef.current
      if (!viewportNode) {
        setZoomLevel(clampZoom(nextZoomLevel))
        return
      }

      const previousZoom = zoomLevel
      const clampedZoom = clampZoom(nextZoomLevel)
      if (previousZoom === clampedZoom) return

      const rect = viewportNode.getBoundingClientRect()
      const pointX =
        typeof clientX === 'number'
          ? clientX - rect.left
          : viewportNode.clientWidth / 2
      const pointY =
        typeof clientY === 'number'
          ? clientY - rect.top
          : viewportNode.clientHeight / 2
      const relativeX = (viewportNode.scrollLeft + pointX) / previousZoom
      const relativeY = (viewportNode.scrollTop + pointY) / previousZoom

      setZoomLevel(clampedZoom)

      requestAnimationFrame(() => {
        const activeViewport = viewportRef.current
        if (!activeViewport) return
        activeViewport.scrollLeft = relativeX * clampedZoom - pointX
        activeViewport.scrollTop = relativeY * clampedZoom - pointY
      })
    },
    [zoomLevel]
  )

  const increaseZoom = () => {
    updateZoom(zoomLevel + ZOOM_STEP)
  }

  const decreaseZoom = () => {
    updateZoom(zoomLevel - ZOOM_STEP)
  }

  const resetZoom = () => {
    updateZoom(1)
  }

  useEffect(() => {
    if (!isPanning) return undefined

    const handlePointerMove = (event) => {
      const viewportNode = viewportRef.current
      const panState = panStateRef.current
      if (!viewportNode || !panState) return

      const deltaX = event.clientX - panState.startX
      const deltaY = event.clientY - panState.startY
      viewportNode.scrollLeft = panState.scrollLeft - deltaX
      viewportNode.scrollTop = panState.scrollTop - deltaY
    }

    const stopPanning = () => {
      panStateRef.current = null
      setIsPanning(false)
    }

    window.addEventListener('mousemove', handlePointerMove)
    window.addEventListener('mouseup', stopPanning)

    return () => {
      window.removeEventListener('mousemove', handlePointerMove)
      window.removeEventListener('mouseup', stopPanning)
    }
  }, [isPanning])

  const beginPan = (event) => {
    const viewportNode = viewportRef.current
    if (!viewportNode || !isAnnotationPanMode || zoomLevel <= 1) return
    if (event.button !== 0) return

    const targetTag = event.target?.tagName?.toLowerCase?.() || ''
    if (['button', 'input', 'textarea'].includes(targetTag)) return

    panStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: viewportNode.scrollLeft,
      scrollTop: viewportNode.scrollTop,
    }
    setIsPanning(true)
    event.preventDefault()
  }

  const handleWheelZoom = (event) => {
    if (!enableAnnotations) return
    const isGestureZoom = event.ctrlKey || event.metaKey
    if (!isGestureZoom) return

    event.preventDefault()
    const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP
    updateZoom(zoomLevel + delta, event.clientX, event.clientY)
  }

  const handleTouchStart = (event) => {
    if (!enableAnnotations) return

    if (event.touches.length === 2) {
      const [touchA, touchB] = event.touches
      touchStateRef.current = {
        distance: getTouchDistance(touchA, touchB),
        zoomLevel,
        midpoint: getTouchMidpoint(touchA, touchB),
      }
      return
    }

    if (event.touches.length === 1 && isAnnotationPanMode && zoomLevel > 1) {
      const viewportNode = viewportRef.current
      const [touch] = event.touches
      if (!viewportNode || !touch) return

      panStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        scrollLeft: viewportNode.scrollLeft,
        scrollTop: viewportNode.scrollTop,
      }
      setIsPanning(true)
    }
  }

  const handleTouchMove = (event) => {
    if (!enableAnnotations) return

    if (event.touches.length === 2 && touchStateRef.current) {
      const [touchA, touchB] = event.touches
      const nextDistance = getTouchDistance(touchA, touchB)
      const scaleRatio = nextDistance / touchStateRef.current.distance
      const midpoint = getTouchMidpoint(touchA, touchB)
      event.preventDefault()
      updateZoom(
        touchStateRef.current.zoomLevel * scaleRatio,
        midpoint.x,
        midpoint.y
      )
      return
    }

    if (event.touches.length === 1 && isPanning && panStateRef.current) {
      const viewportNode = viewportRef.current
      const [touch] = event.touches
      if (!viewportNode || !touch) return

      event.preventDefault()
      viewportNode.scrollLeft =
        panStateRef.current.scrollLeft -
        (touch.clientX - panStateRef.current.startX)
      viewportNode.scrollTop =
        panStateRef.current.scrollTop -
        (touch.clientY - panStateRef.current.startY)
    }
  }

  const handleTouchEnd = () => {
    touchStateRef.current = null
    panStateRef.current = null
    setIsPanning(false)
  }

  const overlayButtonStyle = (isActive, accentColor, m) => ({
    width: m === 'OVAL' ? CONTROLLER_BUTTON_SIZE : CONTROLLER_BUTTON_SIZE,
    height: CONTROLLER_BUTTON_SIZE,
    minWidth: CONTROLLER_BUTTON_SIZE,
    minHeight: CONTROLLER_BUTTON_SIZE,
    padding: 0,
    borderRadius: 8,
    background: isActive ? accentColor : '#ffffff',
    color: isActive ? '#ffffff' : accentColor || INACTIVE_TOOL_COLOR,
    boxShadow: 'none',
    border: isActive ? '1px solid transparent' : '1px solid #e5e7eb',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: isActive ? accentColor : '#f9fafb',
    },
  })

  return (
    <Card
      style={{
        height: enableAnnotations ? '100%' : 'calc(100% - 24px)',
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid #bdbdbd',
        background: '#f5f5f5',
        boxShadow: 'none',
      }}
      className={props.rootClass}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        {enableAnnotations && !!title && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 12px',
              borderBottom: '1px solid #d7d7d7',
              background: '#ffffff',
              gap: 8,
            }}
          >
            <Typography
              style={{
                color: '#333',
                fontWeight: '500',
              }}
            >
              {title}
            </Typography>
          </div>
        )}
        <div
          style={{
            position: 'relative',
            flex: 1,
            height: annotationViewportHeight,
            minHeight: annotationViewportHeight,
          }}
        >
          <div
            ref={viewportRef}
            onMouseDown={beginPan}
            onWheel={handleWheelZoom}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              position: 'relative',
              height: '100%',
              minHeight: '100%',
              overflow: 'auto',
              background: enableAnnotations ? '#4b4b4b' : '#ffffff',
              cursor:
                isAnnotationPanMode && zoomLevel > 1
                  ? isPanning
                    ? 'grabbing'
                    : 'grab'
                  : 'default',
              touchAction: enableAnnotations ? 'none' : 'auto',
              ...annotationViewportStyle,
            }}
          >
            <div
              style={{
                height: Math.max(
                  viewportSize.height || 0,
                  displayHeight + CANVAS_PADDING * 2
                ),
                width: Math.max(
                  viewportSize.width || 0,
                  displayWidth + CANVAS_PADDING * 2
                ),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: enableAnnotations ? '24px' : 0,
                boxSizing: 'border-box',
              }}
            >
              <div
                ref={containerRef}
                style={{
                  position: 'relative',
                  width: displayWidth,
                  height: displayHeight,
                  flexShrink: 0,
                  background: enableAnnotations ? '#ffffff' : 'transparent',
                  boxShadow: enableAnnotations
                    ? '0 2px 8px rgba(0, 0, 0, 0.35)'
                    : 'none',
                }}
              >
                <img
                  alt={title}
                  onClick={!enableAnnotations ? handleZoom : undefined}
                  className={props.mediaClass}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    cursor: enableAnnotations ? 'crosshair' : 'zoom-in',
                    objectFit: 'contain',
                    pointerEvents: enableAnnotations ? 'none' : 'auto',
                    background: '#ffffff',
                  }}
                  src={imageSrc}
                  onLoad={(event) => {
                    const { naturalWidth = 0, naturalHeight = 0 } =
                      event.target || {}
                    setNaturalSize({
                      width: naturalWidth,
                      height: naturalHeight,
                    })
                  }}
                />
                <ImageAnnotations
                  containerRef={containerRef}
                  enableAnnotations={enableAnnotations}
                  annotations={resolvedAnnotations}
                  onAnnotationsChange={handleAnnotationsChange}
                  userInfo={userInfo}
                  onZoom={handleZoom}
                  mode={mode}
                />
              </div>
            </div>
          </div>
          {enableAnnotations && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '6px 0',
                  boxShadow: '0 2px 12px rgba(15, 23, 42, 0.1)',
                }}
              >
                {ANNOTATION_MODES.map((m) => {
                  const IconComponent = MODE_ICONS[m]
                  const isActive = mode === m
                  const isLastMode =
                    m === ANNOTATION_MODES[ANNOTATION_MODES.length - 1]
                  return (
                    <Box
                      key={m}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '4px 8px',
                        borderBottom: isLastMode ? 'none' : '1px solid #eceff3',
                      }}
                    >
                      <Box style={{ position: 'relative' }}>
                        <Tooltip
                          title={MODE_TOOLTIPS[m] || m.replace('_', ' ')}
                          placement="right"
                        >
                          <IconButton
                            onClick={() => setMode(m)}
                            sx={overlayButtonStyle(
                              isActive,
                              ACTIVE_TOOL_COLOR,
                              m
                            )}
                          >
                            {m === 'OVAL' ? (
                              <svg viewBox="0 0 100 100">
                                <ellipse
                                  cx="50"
                                  cy="50"
                                  rx="40"
                                  ry="20"
                                  fill={ACTIVE_TOOL_COLOR}
                                  stroke="currentColor"
                                  strokeWidth="6"
                                />
                              </svg>
                            ) : (
                              <IconComponent />
                            )}
                          </IconButton>
                        </Tooltip>
                        {m === 'MOVE' && (
                          <Box
                            style={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              background: '#cc0000',
                              color: '#fff',
                              fontSize: '8px',
                              fontWeight: 700,
                              padding: '2px 4px',
                              borderRadius: '4px',
                              lineHeight: 1,
                              pointerEvents: 'none',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }}
                          >
                            NEW
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </div>

              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '6px 0',
                  boxShadow: '0 2px 12px rgba(15, 23, 42, 0.1)',
                }}
              >
                <Box
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 8px',
                    borderBottom: '1px solid #eceff3',
                  }}
                >
                  <div
                    style={{
                      width: CONTROLLER_BUTTON_SIZE,
                      minHeight: CONTROLLER_BUTTON_SIZE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f9fafb',
                      color: '#111827',
                      fontSize: 11,
                      fontWeight: 700,
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                    }}
                  >
                    {Math.round(zoomLevel * 100)}%
                  </div>
                </Box>

                {[
                  {
                    key: 'zoom-in',
                    title: 'Zoom in',
                    onClick: increaseZoom,
                    IconComponent: ZoomIn,
                  },
                  {
                    key: 'zoom-out',
                    title: 'Zoom out',
                    onClick: decreaseZoom,
                    IconComponent: ZoomOut,
                  },
                  {
                    key: 'reset-zoom',
                    title: 'Reset zoom',
                    onClick: resetZoom,
                    IconComponent: RestartAlt,
                  },
                  ...(zoomSrc
                    ? [
                        {
                          key: 'open-image',
                          title: 'Open image',
                          onClick: handleZoom,
                          IconComponent: ZoomIn,
                        },
                      ]
                    : []),
                ].map((action, index, allActions) => (
                  <Box
                    key={action.key}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '4px 8px',
                      borderBottom:
                        index === allActions.length - 1
                          ? 'none'
                          : '1px solid #eceff3',
                    }}
                  >
                    <Tooltip title={action.title} placement="left">
                      <IconButton
                        onClick={action.onClick}
                        sx={overlayButtonStyle(false, INACTIVE_TOOL_COLOR)}
                      >
                        <action.IconComponent />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </div>
            </>
          )}
        </div>
        {!enableAnnotations && zoomSrc && (
          <button
            type="button"
            onClick={handleZoom}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 3,
              background: '#ffffff',
              border: '1px solid #000000',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Zoom
          </button>
        )}
      </div>
    </Card>
  )
}
